/**
 * #757 — "the requested window is empty, so here is the next one that isn't."
 *
 * One module owns that notion for every board: which slate is shown instead,
 * and how the swap reads on screen. The Moneyline board (#712) and the
 * Spread/Total board reach it from opposite directions — the dashboard derives
 * moneyline rows, while the Spread/Total board derives its own — so the rule
 * lives here rather than in either of them, and the two cannot drift into
 * landing on different days from the same store.
 *
 * What is NOT here: which rows a board is eligible to show. Sport, circuit, CFB
 * subdivision, status, archived/final, and Poly-only lines are each board's own
 * business and are applied by the caller BEFORE the rows arrive — a fallback
 * that picked a date first would land on a day whose only rows the board then
 * filters away, and show an empty board under a banner promising games.
 *
 * Whether a row HAS a day at all is the exception, and it is not eligibility:
 * it is what makes the answer meaningful at all, so it belongs to the rule
 * rather than to each board. See `pickUpcomingSlate`.
 */

import type { SlateRange } from './types';
import {
  formatSlateRange,
  isSlateDate,
  isoToMdy,
  slateWindow,
  slateWindowContains,
  startOfIsoDayMs,
  type SlateWindow,
} from './weeklySlate';

/** The slate a board fell forward to: the window it lands on, and the rows
 *  inside it, in board order. */
export interface UpcomingSlate<T> {
  window: SlateWindow;
  rows: T[];
}

/**
 * The next slate with rows at or after `fromIso`, or null when there is none.
 *
 * Only the FIRST day with rows is taken, then widened to the board's window —
 * so a dense daily sport shows one day's slate rather than a pile of every
 * future day, and a sparse one still isn't a dead end when its next game is two
 * months out. Week mode falls forward a whole window rather than a day (#712):
 * the rows on screen have to be the same shape as the view they stand in for,
 * or everything around them would say week over a single day's rows.
 *
 * `sortMs` decides what "at or after" means (an event's start instant, falling
 * back to its own midnight), and orders the result; `tieBreak` settles rows that
 * start together.
 *
 * ROWS WITHOUT A CALENDAR DATE CANNOT REACH A SLATE — not as the day it lands
 * on, and not among the rows returned; when none are left dateable the answer
 * is null. Left in, one would take the slate over: `''` sorts before every real
 * date, so it wins the earliest-date comparison, and the window it then anchors
 * contains its own kind and nothing else — a board of dateless rows under a
 * banner naming no day, the one shape the copy was written to avoid. That is
 * the helper's own rule rather than a precondition each caller restates, so a
 * feed that genuinely carries dateless rows inherits the guarantee instead of
 * re-deriving it. #765.
 */
export function pickUpcomingSlate<T>(params: {
  rows: readonly T[];
  /** First day of the empty window the trader asked for. */
  fromIso: string;
  range: SlateRange;
  dateOf: (row: T) => string;
  sortMs: (row: T) => number;
  tieBreak: (row: T) => string;
}): UpcomingSlate<T> | null {
  const { rows, fromIso, range, dateOf, sortMs, tieBreak } = params;
  const dayStartMs = startOfIsoDayMs(fromIso);
  if (!Number.isFinite(dayStartMs)) return null;
  // One pass settles both admissions. The requested window is empty (the caller
  // checked), so anything at or after its first day necessarily falls after it —
  // in either mode. Dateability is checked HERE rather than later because every
  // step below reads `dateOf`: the earliest-date choice, the window built from
  // it, and the membership test that fills it.
  const upcoming = rows.filter((row) => isSlateDate(dateOf(row)) && sortMs(row) >= dayStartMs);
  if (upcoming.length === 0) return null;
  const nextDate = upcoming.reduce(
    (soonest, row) => (dateOf(row) < soonest ? dateOf(row) : soonest),
    dateOf(upcoming[0]),
  );
  const window = slateWindow(nextDate, range);
  const landed = upcoming
    .filter((row) => slateWindowContains(window, dateOf(row)))
    .sort((a, b) => {
      const startCmp = sortMs(a) - sortMs(b);
      if (startCmp !== 0) return startCmp;
      return tieBreak(a).localeCompare(tieBreak(b));
    });
  return landed.length > 0 ? { window, rows: landed } : null;
}

/**
 * The banner's second sentence — what the board swapped in.
 *
 * Named in the same unit the board is showing: without that a weekly slate
 * would announce "the next day with games" over a week's worth of rows. `noun`
 * is what the board's rows ARE ("games" on Moneyline, "lines" on
 * Spread/Total), so the sentence stays true of what is on screen.
 */
export function upcomingSlateCopy(
  window: SlateWindow | null,
  range: SlateRange,
  noun: string,
): string {
  const unit = range === 'week' ? 'week' : 'day';
  if (!window) return `Showing the next ${unit} with ${noun}.`;
  const landed = range === 'week' ? formatSlateRange(window) : isoToMdy(window.startIso);
  return landed
    ? `Showing the next ${unit} with ${noun} — ${landed}.`
    : `Showing the next ${unit} with ${noun}.`;
}

/**
 * The same fall-forward, said to someone who SEARCHED rather than browsed.
 *
 * #754's results come from the window the board is showing, which on a fallen-
 * forward board is not the one the date pill names. Without this the trader
 * reads a pill saying 09/18 and a scope line saying 09/19 with nothing joining
 * them. It lives here, beside the banner's two halves, so "how we tell a trader
 * the day moved" has ONE home: the banner and the search summary cannot drift
 * into disagreeing about the same window (pinned in `upcomingSlate.test.ts`).
 *
 * Deliberately says nothing about which sport. The banner can name the board's
 * own sport because a browsing trader is on one; a committed query has widened
 * past it, so naming it here would be a false claim about what was searched.
 */
export function upcomingSlateSearchCopy(
  requested: SlateWindow,
  landed: SlateWindow | null,
  range: SlateRange,
): string {
  const asked = slateScopeCopy(requested, range);
  if (!landed) return `No games ${asked}`;
  const to = range === 'week' ? formatSlateRange(landed) : isoToMdy(landed.startIso);
  return to ? `No games ${asked} — searched ${to}` : `No games ${asked}`;
}

/**
 * The banner's first sentence — the window the trader actually asked for,
 * named: `on 09/01/2026`, or `in Aug 31 – Sep 6` in Week mode.
 *
 * A week whose label can't be built degrades to naming its first day rather
 * than rendering a broken range, matching what `formatSlateRange` promises its
 * callers.
 */
export function slateScopeCopy(window: SlateWindow, range: SlateRange): string {
  const label = range === 'week' ? formatSlateRange(window) : null;
  return label ? `in ${label}` : `on ${isoToMdy(window.startIso)}`;
}
